import {
  CheckboxYesNo,
  DropdownWithRemoteSearch,
  formatCurrency,
  FormContainer,
  LabeledInput,
  Worksheet
} from "uikit"
import React from "react"
import { Dropdown } from "semantic-ui-react"
import { useStore } from "store"
import { AddEmployerModal } from "../../../employers/AddEmployerModal"
import { AddPersonModal } from "../../../people/AddPersonModal"
import { monthOptions, paymentFrequencyOptions } from "../../dropDownOptions"
import { Footer } from "./Footer"
import { PAY_FREQUENCY_DIVISOR } from "components/incomes/dropDownOptions"

const PAYMENT_FREQUENCY_LABEL = paymentFrequencyOptions.reduce(
  (labels, option) => {
    labels[option.value] = option.text
    return labels
  },
  {}
)

const EmploymentIncomeForm = ({
  data: {
    fields: { employer_id, person_id, base_amount, pay_frequency, bonus },
    isModified,
    onSubmit,
    error
  },
  submitLabel = "Done",
  isNew,
  onDelete,
  ownerOptions,
  employerOptions,
  addNewPerson,
  searchEmployers,
  addEmployer,
  isCOVID
}) => (
  <Worksheet error={error}>
    <Worksheet.Body>
      <Worksheet.Row
        label="Employer"
        description={
          employer_id.value === "e00000000001"
            ? "Your future employer, when the storm has passed and things get back to normal (whenever that might be)."
            : ""
        }
        paddedField
      >
        <Worksheet.Field error={employer_id.error}>
          <DropdownWithRemoteSearch
            {...employer_id}
            data-testid="employer"
            addItemValue="__ADD__"
            placeholder="Employer"
            options={employerOptions}
            search
            selection
            remoteSearch={async query => {
              const { employers: searchResults } = await searchEmployers(query)
              return searchResults.map(employer => ({
                text: employer.name,
                value: employer.id
              }))
            }}
          />
          {employer_id.value === "__ADD__" && (
            <AddEmployerModal
              submit={employerValues =>
                addEmployer(employerValues).then(({ employerId }) =>
                  employer_id.onChange(null, { value: employerId })
                )
              }
              onCancel={() => employer_id.onChange(null, { value: null })}
            />
          )}
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row label="Person earning the income" paddedField>
        <Worksheet.Field error={person_id.error}>
          <Dropdown
            {...person_id}
            placeholder="Person"
            options={ownerOptions}
            selection
            data-testid="incomeEarner"
          />

          {person_id.value === "__ADD__" && (
            <AddPersonModal
              submit={personValues =>
                addNewPerson(personValues).then(({ personId }) =>
                  person_id.onChange(null, { value: personId })
                )
              }
              onCancel={() => person_id.onChange(null, { value: null })}
            />
          )}
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row
        label="Annual base salary (pre-tax)"
        description="Expected FULL YEAR earnings during a normal year before taxes and deductions, excluding bonuses."
        paddedField
      >
        <Worksheet.Field error={base_amount.error}>
          <LabeledInput
            {...base_amount}
            label=""
            iconText="$"
            iconPosition="left"
            placeholder="0"
          />
        </Worksheet.Field>
      </Worksheet.Row>
      {employer_id.value !== "e00000000001" && (
        <Worksheet.Row label="How frequently is it paid?" paddedField>
          <Worksheet.Field error={pay_frequency.error}>
            <Dropdown
              placeholder="Frequency"
              selection
              options={paymentFrequencyOptions}
              data-testid="pay_frequency"
              {...pay_frequency}
            />
            {base_amount.value && pay_frequency.value && (
              <span style={{ fontWeight: "normal" }}>
                Approximately{" "}
                {formatCurrency(
                  Math.floor(
                    base_amount.object /
                      PAY_FREQUENCY_DIVISOR[pay_frequency.object]
                  )
                )}{" "}
                gross pay{" "}
                {PAYMENT_FREQUENCY_LABEL[pay_frequency.value].toLowerCase()}
              </span>
            )}
          </Worksheet.Field>
        </Worksheet.Row>
      )}
      {employer_id.value !== "e00000000001" && (
        <Worksheet.Row label="Is a bonus typically given?">
          <Worksheet.Field>
            <CheckboxYesNo
              data-testid="hasBonus"
              fluid
              checked={!!bonus.isToggledOn}
              toggle={bonus.toggle}
              name="bonus"
            />
          </Worksheet.Field>
          {bonus.isToggledOn ? (
            <Worksheet.RowCollection>
              <Worksheet.Row
                paddedField
                label="In what month is the bonus typically paid?"
              >
                <Worksheet.Field padded error={bonus.fields.month.error}>
                  <Dropdown
                    placeholder="month"
                    search
                    selection
                    options={monthOptions}
                    {...bonus.fields.month}
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                paddedField
                label="What's the typical bonus amount?"
              >
                <Worksheet.Field padded error={bonus.fields.amount.error}>
                  <LabeledInput
                    {...bonus.fields.amount}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
            </Worksheet.RowCollection>
          ) : null}
        </Worksheet.Row>
      )}
    </Worksheet.Body>
    <Footer
      isNew={isNew}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
      onDelete={onDelete}
      isModified={isModified}
      deleteLabel="Delete Income"
      error={error}
    />
  </Worksheet>
)

const EmploymentIncomeFormContainer = FormContainer(
  {
    employer_id: {
      type: "string"
    },
    person_id: {
      type: "string"
    },
    base_amount: {
      type: "unit"
    },
    pay_frequency: {
      type: "string",
      defaultValue: "biweekly"
    },
    bonus: {
      type: "object",
      required: false,
      schema: {
        month: {
          type: "string"
        },
        amount: {
          type: "unit"
        }
      }
    },
    type: {
      type: "string",
      defaultValue: "employment"
    }
  },
  (fields, ...form) => {}
)(EmploymentIncomeForm)

const EmploymentIncome = ({ household, income, ...formProps }) => {
  const [peopleObject] = useStore("householdPeople", household.id)
  const [subscription, { addAccount, addBenefit }] = useStore(
    "subscriptions",
    household.id
  )
  const [
    employersObject,
    { addPerson, searchEmployers, addEmployer }
  ] = useStore("householdEmployers", household.id)

  const isCOVID =
    subscription && subscription.features && subscription.features.is_covid
  if (isCOVID && !employersObject["e00000000001"]) {
    employersObject["e00000000001"] = {
      id: "e00000000001",
      name: "Future Employer"
    }
  }
  const employers = Object.values(employersObject)
  const people = Object.values(peopleObject)
  const isNew = income === null
  const values = isNew
    ? {
        employer_id: employers.length === 1 ? employers[0].id : null,
        person_id: people.length === 1 ? people[0].id : null
      }
    : income
  const employerOptions = employers.map(({ id: value, name: text }) => ({
    value,
    text
  }))
  if (isCOVID && isNew) {
    const basicSubmit = formProps.submit
    formProps.submit = async values => {
      const income = await basicSubmit(values)
      if (values.employer_id === "e00000000001") {
        const account = await addAccount({
          household_id: household.id,
          type: "401k",
          person_id: values.person_id,
          balance: "0"
        })
        await addBenefit({
          household_id: household.id,
          type: "401k_plan",
          income_id: income.id,
          person_id: values.person_id,
          account_id: account.id,
          match_rate_1: 100,
          match_limit_1: 3
        })
      }
    }
  }
  const canAddEmployer = true
  const ownerOptions = people
    .filter(({ role }) => role === "spouse" || role === "client")
    .map(({ id: value, first_name: text }) => ({
      value,
      text
    }))
  const canAddOwner = false // true
  if (canAddOwner) {
    ownerOptions.push({ text: "Add person...", value: "__ADD__" })
  }
  const addNewPerson = values =>
    addPerson({ household_id: household.id, ...values })
  if (canAddEmployer) {
    employerOptions.push({
      text: "Add Employer...",
      value: "__ADD__"
    })
  }
  return (
    <EmploymentIncomeFormContainer
      {...{
        values,
        employerOptions,
        ownerOptions,
        addNewPerson,
        searchEmployers,
        addEmployer: values =>
          addEmployer({ household_id: household.id, ...values }),
        isCOVID,
        ...formProps
      }}
    />
  )
}

export default EmploymentIncome
